import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Euro, 
  Calendar, 
  Percent, 
  Send, 
  Pause, 
  Play,
  Trash2,
  Plus,
  History
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Slider } from "../components/ui/slider";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter
} from "../components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DebtDetail() {
  const { debtId } = useParams();
  const navigate = useNavigate();
  const [debt, setDebt] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editAmountOpen, setEditAmountOpen] = useState(false);
  const [newPayment, setNewPayment] = useState({ amount: "", note: "" });
  const [editData, setEditData] = useState({ daily_interest_rate: 0, description: "" });
  const [newAmount, setNewAmount] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [debtRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/debts/${debtId}`),
        axios.get(`${API}/payments/${debtId}`)
      ]);
      setDebt(debtRes.data);
      setPayments(paymentsRes.data);
      setEditData({
        daily_interest_rate: debtRes.data.daily_interest_rate,
        description: debtRes.data.description
      });
      setNewAmount(debtRes.data.current_amount.toString());
    } catch (error) {
      toast.error("Errore nel caricamento");
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [debtId, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddPayment = async () => {
    if (!newPayment.amount || parseFloat(newPayment.amount) <= 0) {
      toast.error("Inserisci un importo valido");
      return;
    }
    try {
      await axios.post(`${API}/payments`, {
        debt_id: debtId,
        amount: parseFloat(newPayment.amount),
        note: newPayment.note
      });
      toast.success("Pagamento registrato!");
      setPaymentOpen(false);
      setNewPayment({ amount: "", note: "" });
      fetchData();
    } catch (error) {
      toast.error("Errore nella registrazione");
    }
  };

  const handleUpdateDebt = async () => {
    try {
      await axios.patch(`${API}/debts/${debtId}`, editData);
      toast.success("Debito aggiornato!");
      setEditOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleUpdateAmount = async () => {
    if (!newAmount || parseFloat(newAmount) < 0) {
      toast.error("Inserisci un importo valido");
      return;
    }
    try {
      await axios.patch(`${API}/debts/${debtId}`, { 
        current_amount: parseFloat(newAmount) 
      });
      toast.success("Importo aggiornato!");
      setEditAmountOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const togglePause = async () => {
    try {
      await axios.patch(`${API}/debts/${debtId}`, { is_paused: !debt.is_paused });
      toast.success(debt.is_paused ? "Promemoria riattivati" : "Promemoria in pausa");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const sendMessage = async () => {
    try {
      const response = await axios.post(`${API}/messages/send/${debtId}`);
      if (response.data.status === "sent") {
        toast.success("Messaggio inviato!");
      } else {
        toast.error("Invio fallito");
      }
    } catch (error) {
      toast.error("Errore nell'invio");
    }
  };

  const deleteDebt = async () => {
    try {
      await axios.delete(`${API}/debts/${debtId}`);
      toast.success("Debito eliminato");
      navigate("/");
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  if (loading || !debt) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
  const progressPercent = debt.original_amount > 0 
    ? Math.min(100, (totalPaid / debt.original_amount) * 100) 
    : 0;

  return (
    <div className="min-h-screen p-4 md:p-8 lg:p-12 relative noise-overlay">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => navigate("/")}
          data-testid="back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl md:text-4xl font-bold">{debt.contact_name}</h1>
          <p className="text-muted-foreground">{debt.contact_phone}</p>
        </div>
        <div className="flex gap-2">
          {debt.is_paused && (
            <Badge variant="outline" className="text-yellow-500 border-yellow-500/50">
              <Pause className="h-3 w-3 mr-1" />
              Pausa
            </Badge>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Amount Card */}
          <Card className="bg-card border-border" data-testid="amount-card">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Importo Attuale</p>
                  <div className="font-mono text-5xl md:text-6xl font-bold text-destructive glow-red">
                    €{debt.current_amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                  </div>
                  {debt.current_amount !== debt.original_amount && (
                    <p className="text-muted-foreground mt-2">
                      Originale: <span className="line-through">€{debt.original_amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</span>
                    </p>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-primary text-primary-foreground rounded-full h-12 sm:h-auto" data-testid="add-payment-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Registra Pagamento
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-card border-border sm:max-w-[425px] w-[95vw]">
                      <DialogHeader>
                        <DialogTitle>Registra Pagamento</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Importo (€)</Label>
                          <Input 
                            type="number" 
                            placeholder="0.00"
                            value={newPayment.amount}
                            onChange={(e) => setNewPayment({...newPayment, amount: e.target.value})}
                            className="font-mono text-lg h-12"
                            data-testid="payment-amount-input"
                          />
                          <p className="text-xs text-muted-foreground">
                            Debito attuale: €{debt.current_amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label>Nota (opzionale)</Label>
                          <Input 
                            placeholder="Es: Pagamento parziale..."
                            value={newPayment.note}
                            onChange={(e) => setNewPayment({...newPayment, note: e.target.value})}
                            className="h-12"
                            data-testid="payment-note-input"
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button onClick={handleAddPayment} className="bg-primary text-primary-foreground rounded-full h-12 w-full sm:w-auto" data-testid="confirm-payment-btn">
                          Conferma
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={editAmountOpen} onOpenChange={setEditAmountOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="rounded-full h-12 sm:h-auto" data-testid="edit-amount-btn">
                        <Euro className="h-4 w-4 mr-2" />
                        Modifica Importo
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-card border-border sm:max-w-[425px] w-[95vw]">
                      <DialogHeader>
                        <DialogTitle>Modifica Importo Debito</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Nuovo Importo (€)</Label>
                          <Input 
                            type="number" 
                            placeholder="0.00"
                            value={newAmount}
                            onChange={(e) => setNewAmount(e.target.value)}
                            className="font-mono text-lg h-12"
                            data-testid="edit-amount-input"
                          />
                          <p className="text-xs text-muted-foreground">
                            Importo attuale: €{debt.current_amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                          </p>
                        </div>
                        <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                          <p className="text-sm text-yellow-600 dark:text-yellow-400">
                            ⚠️ Questa operazione modificherà direttamente l'importo del debito. Per registrare un pagamento, usa "Registra Pagamento".
                          </p>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button onClick={handleUpdateAmount} className="bg-primary text-primary-foreground rounded-full h-12 w-full sm:w-auto" data-testid="confirm-edit-amount-btn">
                          Aggiorna Importo
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              {/* Progress bar */}
              <div className="mt-6">
                <div className="flex justify-between text-sm text-muted-foreground mb-2">
                  <span>Progresso pagamento</span>
                  <span>{progressPercent.toFixed(1)}%</span>
                </div>
                <div className="h-2 bg-secondary rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-500"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Pagato: €{totalPaid.toLocaleString('it-IT', { minimumFractionDigits: 2 })} / €{debt.original_amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Details Card */}
          <Card className="bg-card border-border" data-testid="details-card">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Dettagli</span>
                <Dialog open={editOpen} onOpenChange={setEditOpen}>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="sm" data-testid="edit-debt-btn">
                      Modifica
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border-border">
                    <DialogHeader>
                      <DialogTitle>Modifica Debito</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Interesse Giornaliero: {editData.daily_interest_rate}%</Label>
                        <Slider
                          value={[editData.daily_interest_rate]}
                          onValueChange={(v) => setEditData({...editData, daily_interest_rate: v[0]})}
                          max={5}
                          step={0.1}
                          className="py-4"
                          data-testid="edit-interest-slider"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Descrizione</Label>
                        <Input 
                          value={editData.description}
                          onChange={(e) => setEditData({...editData, description: e.target.value})}
                          data-testid="edit-description-input"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button onClick={handleUpdateDebt} className="bg-primary text-primary-foreground rounded-full" data-testid="confirm-edit-btn">
                        Salva
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-secondary/50">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Percent className="h-4 w-4" />
                    <span className="text-sm">Interesse</span>
                  </div>
                  <p className="font-mono text-xl font-bold">{debt.daily_interest_rate}%/giorno</p>
                </div>
                <div className="p-4 rounded-lg bg-secondary/50">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Calendar className="h-4 w-4" />
                    <span className="text-sm">Creato il</span>
                  </div>
                  <p className="font-mono text-lg">{new Date(debt.created_at).toLocaleDateString('it-IT')}</p>
                </div>
              </div>
              {debt.description && (
                <div className="p-4 rounded-lg bg-secondary/50">
                  <p className="text-sm text-muted-foreground mb-1">Descrizione</p>
                  <p>{debt.description}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          <Card className="bg-card border-border" data-testid="actions-card">
            <CardHeader>
              <CardTitle>Azioni</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                className="w-full bg-primary text-primary-foreground rounded-full h-12"
                onClick={sendMessage}
                data-testid="send-reminder-btn"
              >
                <Send className="h-4 w-4 mr-2" />
                Invia Promemoria
              </Button>
              <Button 
                variant="secondary"
                className="w-full rounded-full h-12"
                onClick={togglePause}
                data-testid="toggle-pause-btn"
              >
                {debt.is_paused ? (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Riattiva Promemoria
                  </>
                ) : (
                  <>
                    <Pause className="h-4 w-4 mr-2" />
                    Metti in Pausa
                  </>
                )}
              </Button>
              <Button 
                variant="ghost"
                className="w-full rounded-full h-12 text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={deleteDebt}
                data-testid="delete-debt-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Elimina Debito
              </Button>
            </CardContent>
          </Card>

          {/* Payment History */}
          <Card className="bg-card border-border" data-testid="payment-history-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5 text-primary" />
                Storico Pagamenti
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                {payments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Euro className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Nessun pagamento</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {payments.map((payment, index) => (
                      <div 
                        key={payment.id}
                        className="p-3 rounded-lg bg-secondary/30 animate-fadeIn"
                        style={{ animationDelay: `${index * 50}ms` }}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-mono font-bold text-primary">
                              +€{payment.amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                            </p>
                            {payment.note && (
                              <p className="text-xs text-muted-foreground mt-1">{payment.note}</p>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {new Date(payment.created_at).toLocaleDateString('it-IT')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
